```vue
<template>
    <v-app>
        <v-main class="main">

            <!-- Sidebar -->
            <Sidebar
                :drawerOpen="drawerOpen"
                :collapsed="isSidebarCollapsed"
                :isMobile="isMobile"
                @update:drawerOpen="drawerOpen = $event"
            />

            <!-- Main Content -->
            <v-container fluid class="main-content pa-6">

                <!-- Header -->
                <Header
                    :isDarkMode="isDarkMode"
                    :isMobile="isMobile"
                    @toggle-sidebar="toggleSidebar"
                    @toggle-darkmode="toggleDarkMode"
                />

                <!-- Loading -->
                <div v-if="loading" class="loader">
                    <v-progress-circular
                        indeterminate
                        color="primary"
                    />
                </div>

                <div class="content">

                    <v-row class="g-4 mt-4 mb-8" dense>

                        <!-- ============================= -->
                        <!-- ACCOUNT SETTINGS -->
                        <!-- ============================= -->

                        <v-col cols="12">
                            <div class="column-title mb-6">
                                <h5 class="m-0 text-left">
                                    Account Settings
                                </h5>
                                <hr class="title-separator" />
                            </div>
                        </v-col>

                        <GlobalKyc />

                        <!-- ============================= -->
                        <!-- BANK DETAILS -->
                        <!-- ============================= -->

                        <v-col cols="12" class="pa-2">

                            <Panel
                                toggleable
                                v-model:collapsed="bankCollapsed"
                            >

                                <template #header>
                                    <div class="flex items-center gap-2">

                                        <Button
                                            icon="pi pi-user"
                                            severity="secondary"
                                            rounded
                                            text
                                            style="color: green; font-size: 1.5rem;"
                                        />

                                        <span class="font-bold">
                                            Bank Details
                                        </span>

                                    </div>
                                </template>

                                <template #icons>
                                    <Menu
                                        ref="menu"
                                        id="config_menu"
                                        :model="items"
                                        popup
                                    />
                                </template>

                                <div class="pa-10">

                                    <v-row class="text-left">

                                        <!-- Bank -->
                                        <v-col cols="12" md="6">

                                            <div class="field">

                                                <label>
                                                    Bank Name
                                                </label>

                                                <input
                                                    v-model="bank"
                                                    type="text"
                                                    class="modern-input"
                                                    readonly
                                                />

                                            </div>

                                        </v-col>

                                        <!-- Account Number -->
                                        <v-col cols="12" md="6">

                                            <div class="field">

                                                <label>
                                                    Account Number
                                                </label>

                                                <input
                                                    v-model="nuban"
                                                    type="text"
                                                    class="modern-input"
                                                    readonly
                                                />

                                            </div>

                                        </v-col>

                                        <!-- Account Name -->
                                        <v-col cols="12">

                                            <div class="field">

                                                <label>
                                                    Account Name
                                                </label>

                                                <input
                                                    v-model="bankAccountName"
                                                    type="text"
                                                    class="modern-input"
                                                    readonly
                                                />

                                            </div>

                                        </v-col>

                                    </v-row>

                                </div>

                            </Panel>

                        </v-col>


                        <!-- ============================= -->
                        <!-- SECURITY SETTINGS -->
                        <!-- ============================= -->

                        <v-col cols="12" class="pa-2">

                            <Panel
                                toggleable
                                v-model:collapsed="securityCollapsed"
                            >

                                <template #header>

                                    <div class="flex items-center gap-2">

                                        <Button
                                            icon="pi pi-users"
                                            severity="secondary"
                                            rounded
                                            text
                                            style="color: green; font-size: 1.5rem;"
                                        />

                                        <span class="font-bold">
                                            Security Settings
                                        </span>

                                    </div>

                                </template>

                                <template #icons>

                                    <Menu
                                        ref="menu"
                                        id="config_menu"
                                        :model="items"
                                        popup
                                    />

                                </template>


                                <v-card
                                    class="outer-card"
                                    flat
                                >

                                    <v-row
                                        dense
                                        class="g-2"
                                    >

                                        <v-col cols="12">

                                            <v-row dense>


                                                <!-- ================================= -->
                                                <!-- PASSWORD -->
                                                <!-- ================================= -->

                                                <v-col
                                                    cols="12"
                                                    md="6"
                                                    class="d-flex flex-column"
                                                >

                                                    <v-card
                                                        class="inner-mini-card flex-grow-1"
                                                        flat
                                                    >

                                                        <div class="pa-6">

                                                            <div class="column-title mb-6">

                                                                <h5 class="m-0 text-left text-grey">
                                                                    Password Update
                                                                </h5>

                                                                <hr class="title-separator" />

                                                            </div>


                                                            <!-- Error -->
                                                            <v-alert
                                                                v-if="error"
                                                                type="error"
                                                                density="compact"
                                                                class="mb-4"
                                                            >
                                                                {{ error }}
                                                            </v-alert>


                                                            <form
                                                                @submit.prevent="handlePasswordChange"
                                                                class="password-form"
                                                            >

                                                                <!-- Current Password -->
                                                                <div class="password-group">

                                                                    <label class="text-sm font-medium text-gray-700">
                                                                        Current Password
                                                                    </label>

                                                                    <div class="password-wrapper">

                                                                        <input
                                                                            v-model="current_password"
                                                                            :type="showPassword ? 'text' : 'password'"
                                                                            required
                                                                            minlength="8"
                                                                            placeholder="••••••••"
                                                                            class="modern-input password-input"
                                                                        />

                                                                        <v-icon
                                                                            class="password-icon"
                                                                            @click="togglePassword"
                                                                        >
                                                                            {{
                                                                                showPassword
                                                                                    ? 'mdi-eye-off-outline'
                                                                                    : 'mdi-eye-outline'
                                                                            }}
                                                                        </v-icon>

                                                                    </div>

                                                                </div>

                                                                <!-- New Password -->
                                                                <div class="password-group">

                                                                    <label class="text-sm font-medium text-gray-700">
                                                                        New Password
                                                                    </label>

                                                                    <div class="password-wrapper">

                                                                        <input
                                                                            v-model="password"
                                                                            :type="showPassword ? 'text' : 'password'"
                                                                            required
                                                                            minlength="8"
                                                                            placeholder="••••••••"
                                                                            class="modern-input password-input"
                                                                        />

                                                                        <v-icon
                                                                            class="password-icon"
                                                                            @click="togglePassword"
                                                                        >
                                                                            {{
                                                                                showPassword
                                                                                    ? 'mdi-eye-off-outline'
                                                                                    : 'mdi-eye-outline'
                                                                            }}
                                                                        </v-icon>

                                                                    </div>

                                                                </div>


                                                                <!-- Confirm Password -->
                                                                <div class="password-group">

                                                                    <label class="text-sm font-medium text-gray-700">
                                                                        Confirm New Password
                                                                    </label>

                                                                    <div class="password-wrapper">

                                                                        <input
                                                                            v-model="confirmpass"
                                                                            :type="showPassword ? 'text' : 'password'"
                                                                            required
                                                                            minlength="8"
                                                                            placeholder="••••••••"
                                                                            class="modern-input password-input"
                                                                        />

                                                                        <v-icon
                                                                            class="password-icon"
                                                                            @click="togglePassword"
                                                                        >
                                                                            {{
                                                                                showPassword
                                                                                    ? 'mdi-eye-off-outline'
                                                                                    : 'mdi-eye-outline'
                                                                            }}
                                                                        </v-icon>

                                                                    </div>

                                                                </div>


                                                                <!-- Submit -->
                                                                <button
                                                                    type="submit"
                                                                    :disabled="passLoading"
                                                                    class="action-button mt-6"
                                                                >

                                                                    <span v-if="!passLoading">
                                                                        Update Password
                                                                        <v-icon start>
                                                                            mdi-update
                                                                        </v-icon>
                                                                    </span>

                                                                    <span v-else>
                                                                        Updating...
                                                                        <v-icon
                                                                            start
                                                                            class="spin"
                                                                        >
                                                                            mdi-loading
                                                                        </v-icon>
                                                                    </span>

                                                                </button>

                                                            </form>

                                                        </div>

                                                    </v-card>

                                                </v-col>


                                                <!-- ================================= -->
                                                <!-- TRANSACTION PIN -->
                                                <!-- ================================= -->

                                                <v-col
                                                    cols="12"
                                                    md="6"
                                                    class="d-flex"
                                                >

                                                    <v-card
                                                        class="inner-card flex-grow-1"
                                                        flat
                                                    >

                                                        <div class="pa-3">

                                                            <div class="column-title mb-6">

                                                                <h5 class="m-0 text-left text-grey">
                                                                    Update Transaction Pin
                                                                </h5>

                                                                <hr class="title-separator" />

                                                            </div>


                                                            <form
                                                                @submit.prevent="submitPin"
                                                                class="pin-form"
                                                            >

                                                                <!-- PIN Error -->
                                                                <v-alert
                                                                    v-if="pinError"
                                                                    type="error"
                                                                    density="compact"
                                                                    class="mb-5"
                                                                >
                                                                    {{ pinError }}
                                                                </v-alert>


                                                                <!-- ======================== -->
                                                                <!-- CURRENT PIN -->
                                                                <!-- ======================== -->

                                                                <div class="pin-group">

                                                                    <label class="text-sm font-medium text-gray-700">
                                                                        Current Pin
                                                                    </label>

                                                                    <div class="pin-wrapper">

                                                                        <div
                                                                            class="pin-container current-pin"
                                                                        >

                                                                            <input
                                                                                v-for="(digit, index) in current_pin"
                                                                                :key="`current-${index}`"
                                                                                :ref="el => setPinRef(el, 'current_pin', index)"
                                                                                v-model="current_pin[index]"
                                                                                type="password"
                                                                                inputmode="numeric"
                                                                                autocomplete="off"
                                                                                maxlength="1"
                                                                                class="pin-box"
                                                                                @input="handleInput($event, index, 'current_pin')"
                                                                                @keydown="handleKeydown($event, index, 'current_pin')"
                                                                                @paste="handlePaste($event, index, 'current_pin')"
                                                                            />

                                                                        </div>

                                                                    </div>

                                                                </div>


                                                                <!-- ======================== -->
                                                                <!-- NEW PIN -->
                                                                <!-- ======================== -->

                                                                <div class="pin-group">

                                                                    <label class="text-sm font-medium text-gray-700">
                                                                        New Pin
                                                                    </label>

                                                                    <div class="pin-wrapper">

                                                                        <div
                                                                            class="pin-container new-pin"
                                                                        >

                                                                            <input
                                                                                v-for="(digit, index) in pin"
                                                                                :key="`new-${index}`"
                                                                                :ref="el => setPinRef(el, 'pin', index)"
                                                                                v-model="pin[index]"
                                                                                type="password"
                                                                                inputmode="numeric"
                                                                                autocomplete="off"
                                                                                maxlength="1"
                                                                                class="pin-box"
                                                                                @input="handleInput($event, index, 'pin')"
                                                                                @keydown="handleKeydown($event, index, 'pin')"
                                                                                @paste="handlePaste($event, index, 'pin')"
                                                                            />

                                                                        </div>

                                                                    </div>

                                                                </div>


                                                                <!-- ======================== -->
                                                                <!-- CONFIRM PIN -->
                                                                <!-- ======================== -->

                                                                <div class="pin-group">

                                                                    <label class="text-sm font-medium text-gray-700">
                                                                        Confirm New Pin
                                                                    </label>

                                                                    <div class="pin-wrapper">

                                                                        <div
                                                                            class="pin-container confirm-pin"
                                                                        >

                                                                            <input
                                                                                v-for="(digit, index) in confirmPin"
                                                                                :key="`confirm-${index}`"
                                                                                :ref="el => setPinRef(el, 'confirmPin', index)"
                                                                                v-model="confirmPin[index]"
                                                                                type="password"
                                                                                inputmode="numeric"
                                                                                autocomplete="off"
                                                                                maxlength="1"
                                                                                class="pin-box"
                                                                                @input="handleInput($event, index, 'confirmPin')"
                                                                                @keydown="handleKeydown($event, index, 'confirmPin')"
                                                                                @paste="handlePaste($event, index, 'confirmPin')"
                                                                            />

                                                                        </div>

                                                                    </div>

                                                                </div>


                                                                <!-- Submit -->
                                                                <button
                                                                    type="submit"
                                                                    :disabled="pinLoading"
                                                                    class="action-button mt-4"
                                                                >

                                                                    <span v-if="!pinLoading">

                                                                        Update Pin

                                                                        <v-icon start>
                                                                            mdi-update
                                                                        </v-icon>

                                                                    </span>

                                                                    <span v-else>

                                                                        Updating...

                                                                        <v-icon
                                                                            start
                                                                            class="spin"
                                                                        >
                                                                            mdi-loading
                                                                        </v-icon>

                                                                    </span>

                                                                </button>

                                                            </form>

                                                        </div>

                                                    </v-card>

                                                </v-col>


                                                <!-- ================================= -->
                                                <!-- TWO FACTOR -->
                                                <!-- ================================= -->

                                                <v-col
                                                    cols="12"
                                                    md="6"
                                                    class="d-flex"
                                                >

                                                    <v-card
                                                        class="inner-card flex-grow-1"
                                                        flat
                                                    >

                                                        <div class="pa-3">

                                                            <div class="column-title mb-6">

                                                                <h5 class="m-0 text-left text-grey">
                                                                    Two Factor Authentication
                                                                </h5>

                                                                <hr class="title-separator" />

                                                            </div>


                                                            <v-card
                                                                rounded="lg"
                                                                class="pa-6"
                                                            >

                                                                <div
                                                                    class="d-flex align-center justify-space-between"
                                                                >

                                                                    <div>

                                                                        <div class="text-body-2 text-medium-emphasis">

                                                                            Add an extra layer of security to your
                                                                            account.

                                                                        </div>

                                                                    </div>


                                                                    <v-switch
                                                                        v-model="twoFactorEnabled"
                                                                        color="success"
                                                                        inset
                                                                        hide-details
                                                                        :loading="updating2FA"
                                                                        @update:model-value="toggleTwoFactor"
                                                                    />

                                                                </div>

                                                            </v-card>

                                                        </div>

                                                    </v-card>

                                                </v-col>


                                                <!-- ================================= -->
                                                <!-- TIN -->
                                                <!-- ================================= -->

                                                <v-col
                                                    cols="12"
                                                    md="6"
                                                    class="d-flex"
                                                >

                                                    <v-card
                                                        class="inner-card flex-grow-1"
                                                        flat
                                                    >

                                                        <div class="pa-3">

                                                            <div class="column-title mb-6">

                                                                <h5 class="m-0 text-left text-grey">
                                                                    Update TIN
                                                                </h5>

                                                                <hr class="title-separator" />

                                                            </div>


                                                            <form
                                                                @submit.prevent="handleUpdateTIN"
                                                                class="password-form"
                                                            >

                                                                <div class="password-group">

                                                                    <label class="text-sm font-medium text-gray-700">
                                                                        TIN
                                                                    </label>

                                                                    <div class="password-wrapper">

                                                                        <input
                                                                            :value="isTinSuccessful ? maskedTin : tin"
                                                                            @input="handleTinInput"
                                                                            type="text"
                                                                            inputmode="numeric"
                                                                            :disabled="isTinSuccessful"
                                                                            required
                                                                            placeholder="1234567891023"
                                                                            class="modern-input password-input"
                                                                        />

                                                                    </div>

                                                                </div>


                                                                <button
                                                                    type="submit"
                                                                    :disabled="tinLoading || isTinSuccessful"
                                                                    class="action-button"
                                                                >

                                                                    <span v-if="!tinLoading">

                                                                        Update TIN

                                                                        <v-icon start>
                                                                            mdi-update
                                                                        </v-icon>

                                                                    </span>

                                                                    <span v-else>

                                                                        Updating...

                                                                        <v-icon
                                                                            start
                                                                            class="spin"
                                                                        >
                                                                            mdi-loading
                                                                        </v-icon>

                                                                    </span>

                                                                </button>

                                                            </form>

                                                        </div>

                                                    </v-card>

                                                </v-col>

                                            </v-row>

                                        </v-col>

                                    </v-row>

                                </v-card>

                            </Panel>

                        </v-col>


                        <!-- ============================= -->
                        <!-- HELP & SUPPORT -->
                        <!-- ============================= -->

                        <v-col cols="12" class="pa-2">

                            <Panel
                                toggleable
                                v-model:collapsed="helpCollapsed"
                            >

                                <template #header>

                                    <div class="flex items-center gap-2">

                                        <Button
                                            icon="pi pi-users"
                                            severity="secondary"
                                            rounded
                                            text
                                            style="color: green; font-size: 1.5rem;"
                                        />

                                        <span class="font-bold">
                                            Help & Support
                                        </span>

                                    </div>

                                </template>


                                <template #icons>

                                    <Menu
                                        ref="menu"
                                        id="config_menu"
                                        :model="items"
                                        popup
                                    />

                                </template>


                                <v-card
                                    class="outer-card"
                                    flat
                                >

                                    <v-row dense>

                                        <!-- Phone + Social -->
                                        <v-col
                                            cols="12"
                                            md="6"
                                        >

                                            <v-card
                                                class="inner-mini-card mb-3"
                                                flat
                                            >

                                                <div class="pa-3">

                                                    <div class="column-title mb-6">

                                                        <h5 class="m-0 text-left text-grey">
                                                            Phone
                                                        </h5>

                                                        <hr class="title-separator" />

                                                    </div>


                                                    <div class="text-small mt-2">

                                                        <div class="d-flex justify-space-between py-2">

                                                            <span>
                                                                +234 706 0726 012
                                                            </span>

                                                            <v-icon
                                                                size="18"
                                                                class="ms-2 cursor-pointer"
                                                                @click="copyToClipboard('+2347060726012')"
                                                            >
                                                                {{
                                                                    copied === '+2347060726012'
                                                                        ? 'mdi-check'
                                                                        : 'mdi-content-copy'
                                                                }}
                                                            </v-icon>

                                                        </div>


                                                        <div class="d-flex justify-space-between py-2">

                                                            <span>
                                                                +234 704 8582 184
                                                            </span>

                                                            <v-icon
                                                                size="18"
                                                                class="ms-2 cursor-pointer"
                                                                @click="copyToClipboard('+2347048582184')"
                                                            >
                                                                {{
                                                                    copied === '+2347048582184'
                                                                        ? 'mdi-check'
                                                                        : 'mdi-content-copy'
                                                                }}
                                                            </v-icon>

                                                        </div>

                                                    </div>

                                                </div>

                                            </v-card>


                                            <v-card
                                                class="inner-mini-card"
                                                flat
                                            >

                                                <div class="pa-3">

                                                    <div class="column-title mb-6">

                                                        <h5 class="m-0 text-left text-grey">
                                                            Connect with us
                                                        </h5>

                                                        <hr class="title-separator" />

                                                    </div>


                                                    <div class="d-flex ga-2">

                                                        <v-btn
                                                            icon="mdi-facebook"
                                                            variant="text"
                                                            href="https://facebook.com"
                                                            target="_blank"
                                                        />

                                                        <v-btn
                                                            icon="mdi-twitter"
                                                            variant="text"
                                                            href="https://twitter.com"
                                                            target="_blank"
                                                        />

                                                        <v-btn
                                                            icon="mdi-instagram"
                                                            variant="text"
                                                            href="https://instagram.com"
                                                            target="_blank"
                                                        />

                                                        <v-btn
                                                            icon="mdi-linkedin"
                                                            variant="text"
                                                            href="https://linkedin.com"
                                                            target="_blank"
                                                        />

                                                    </div>

                                                </div>

                                            </v-card>

                                        </v-col>


                                        <!-- Email -->
                                        <v-col
                                            cols="12"
                                            md="6"
                                            class="d-flex"
                                        >

                                            <v-card
                                                class="inner-card flex-grow-1"
                                                flat
                                            >

                                                <div class="pa-3">

                                                    <div class="column-title mb-6">

                                                        <h5 class="m-0 text-left text-grey">
                                                            Email
                                                        </h5>

                                                        <hr class="title-separator" />

                                                    </div>


                                                    <div class="d-flex justify-space-between py-2">

                                                        <span>
                                                            info@crowdital.com
                                                        </span>

                                                        <v-icon
                                                            size="18"
                                                            class="ms-2 cursor-pointer"
                                                            @click="copyToClipboard('info@crowdital.com')"
                                                        >
                                                            {{
                                                                copied === 'info@crowdital.com'
                                                                    ? 'mdi-check'
                                                                    : 'mdi-content-copy'
                                                            }}
                                                        </v-icon>

                                                    </div>

                                                </div>

                                            </v-card>

                                        </v-col>

                                    </v-row>

                                </v-card>

                            </Panel>

                        </v-col>


                        <!-- ============================= -->
                        <!-- LOGOUT -->
                        <!-- ============================= -->

                        <v-col cols="12" class="pa-2">

                            <div
                                @click="logout"
                                style="cursor: pointer"
                            >

                                <Panel
                                    toggleable
                                    v-model:collapsed="logoutCollapsed"
                                >

                                    <template #header>

                                        <div class="flex items-center gap-2">

                                            <Button
                                                icon="pi pi-sign-out"
                                                severity="secondary"
                                                rounded
                                                text
                                                style="color: red; font-size: 1.5rem;"
                                            />

                                            <span class="font-bold text-red">
                                                Logout
                                            </span>

                                        </div>

                                    </template>

                                </Panel>

                            </div>

                        </v-col>

                    </v-row>

                </div>

            </v-container>


            <!-- ============================= -->
            <!-- GLOBAL SNACKBAR -->
            <!-- ============================= -->

            <v-snackbar
                v-model="snackbar.show"
                :color="snackbar.color"
                timeout="3000"
                location="top right"
            >

                {{ snackbar.message }}

                <template #actions>

                    <v-btn
                        variant="text"
                        @click="snackbar.show = false"
                    >
                        Close
                    </v-btn>

                </template>

            </v-snackbar>

        </v-main>
    </v-app>
</template>


<script setup>
import {
    ref,
    onMounted,
    onUnmounted,
    computed,
    nextTick
} from "vue"

import Sidebar from "@/components/Sidebar.vue"
import Header from "@/components/Header.vue"
import GlobalKyc from "@/components/GlobalKyc.vue"

import { useDashStore } from "@/store/dashboard"
import { useAuthStore } from "@/store/auth"
import { useRouter } from "vue-router"

import {
    changePassword,
    enable2FA,
    updateTin,
    updateTransactionPin
} from "@/api/auth"

import { getAllErrors } from "@/util"


/* =========================================================
   STORES
========================================================= */

const dash = useDashStore()
const auth = useAuthStore()
const router = useRouter()


/* =========================================================
   GENERAL
========================================================= */

const loading = ref(false)

const isSidebarCollapsed = ref(false)
const isDarkMode = ref(false)
const drawerOpen = ref(false)
const isMobile = ref(false)

const bankCollapsed = ref(true)
const securityCollapsed = ref(true)
const helpCollapsed = ref(true)
const logoutCollapsed = ref(true)


/* =========================================================
   PASSWORD
========================================================= */

const password = ref("")
const confirmpass = ref("")
const current_password = ref("")
const passLoading = ref(false)
const error = ref("")

const showPassword = ref(false)

const togglePassword = () => {
    showPassword.value = !showPassword.value
}


/* =========================================================
   TRANSACTION PIN
========================================================= */

const PIN_LENGTH = 6

const pin = ref(
    Array(PIN_LENGTH).fill("")
)

const confirmPin = ref(
    Array(PIN_LENGTH).fill("")
)

const current_pin = ref(
    Array(PIN_LENGTH).fill("")
)

const pinError = ref("")
const pinLoading = ref(false)


/*
|--------------------------------------------------------------------------
| PIN INPUT REFERENCES
|--------------------------------------------------------------------------
|
| We keep references to all three PIN groups separately.
|
| current_pin
| pin
| confirmPin
|
*/

const pinRefs = {
    current_pin: [],
    pin: [],
    confirmPin: []
}


const setPinRef = (el, type, index) => {

    if (!el) {
        return
    }

    pinRefs[type][index] = el
}


/*
|--------------------------------------------------------------------------
| Get the correct PIN array
|--------------------------------------------------------------------------
*/

const getPinArray = (type) => {

    switch (type) {

        case "current_pin":
            return current_pin

        case "pin":
            return pin

        case "confirmPin":
            return confirmPin

        default:
            return null
    }
}


/*
|--------------------------------------------------------------------------
| Handle PIN input
|--------------------------------------------------------------------------
*/

const handleInput = (event, index, type) => {

    const arr = getPinArray(type)

    if (!arr) {
        return
    }


    /*
    |--------------------------------------------------------------------------
    | Only allow numbers
    |--------------------------------------------------------------------------
    */

    const value = event.target.value
        .replace(/\D/g, "")
        .slice(0, 1)


    arr.value[index] = value

    /*
    |--------------------------------------------------------------------------
    | Keep input synchronized
    |--------------------------------------------------------------------------
    */

    event.target.value = value


    /*
    |--------------------------------------------------------------------------
    | Clear PIN error while typing
    |--------------------------------------------------------------------------
    */

    if (pinError.value) {
        pinError.value = ""
    }


    /*
    |--------------------------------------------------------------------------
    | Automatically move to next box
    |--------------------------------------------------------------------------
    */

    if (
        value &&
        index < arr.value.length - 1
    ) {

        nextTick(() => {

            pinRefs[type][index + 1]?.focus()

        })

    }

}


/*
|--------------------------------------------------------------------------
| Handle keyboard
|--------------------------------------------------------------------------
*/

const handleKeydown = (event, index, type) => {

    const arr = getPinArray(type)

    if (!arr) {
        return
    }


    const boxes = pinRefs[type]


    /*
    |--------------------------------------------------------------------------
    | BACKSPACE
    |--------------------------------------------------------------------------
    */

    if (event.key === "Backspace") {

        event.preventDefault()


        /*
        |--------------------------------------------------------------------------
        | If current box has a value, clear it.
        |--------------------------------------------------------------------------
        */

        if (arr.value[index]) {

            arr.value[index] = ""

            return
        }


        /*
        |--------------------------------------------------------------------------
        | If current box is empty, go to previous box
        |--------------------------------------------------------------------------
        */

        if (index > 0) {

            arr.value[index - 1] = ""

            nextTick(() => {

                boxes[index - 1]?.focus()

            })

        }

        return
    }


    /*
    |--------------------------------------------------------------------------
    | LEFT ARROW
    |--------------------------------------------------------------------------
    */

    if (
        event.key === "ArrowLeft" &&
        index > 0
    ) {

        event.preventDefault()

        boxes[index - 1]?.focus()

        return
    }


    /*
    |--------------------------------------------------------------------------
    | RIGHT ARROW
    |--------------------------------------------------------------------------
    */

    if (
        event.key === "ArrowRight" &&
        index < arr.value.length - 1
    ) {

        event.preventDefault()

        boxes[index + 1]?.focus()

        return
    }


    /*
    |--------------------------------------------------------------------------
    | HOME
    |--------------------------------------------------------------------------
    */

    if (event.key === "Home") {

        event.preventDefault()

        boxes[0]?.focus()

        return
    }


    /*
    |--------------------------------------------------------------------------
    | END
    |--------------------------------------------------------------------------
    */

    if (event.key === "End") {

        event.preventDefault()

        boxes[arr.value.length - 1]?.focus()

        return
    }


    /*
    |--------------------------------------------------------------------------
    | Prevent non-numeric characters
    |--------------------------------------------------------------------------
    */

    if (
        event.key.length === 1 &&
        !/[0-9]/.test(event.key)
    ) {

        event.preventDefault()

    }

}


/*
|--------------------------------------------------------------------------
| Handle PIN paste
|--------------------------------------------------------------------------
|
| Allows someone to paste:
|
| 123456
|
| into any PIN box.
|
*/

const handlePaste = (event, index, type) => {

    event.preventDefault()

    const arr = getPinArray(type)

    if (!arr) {
        return
    }


    const pastedText = (
        event.clipboardData?.getData("text") || ""
    )
        .replace(/\D/g, "")
        .slice(0, PIN_LENGTH - index)


    if (!pastedText) {
        return
    }


    /*
    |--------------------------------------------------------------------------
    | Put pasted digits into boxes
    |--------------------------------------------------------------------------
    */

    pastedText
        .split("")
        .forEach((digit, offset) => {

            arr.value[index + offset] = digit

        })


    /*
    |--------------------------------------------------------------------------
    | Focus last populated box / next box
    |--------------------------------------------------------------------------
    */

    const nextIndex = Math.min(
        index + pastedText.length,
        PIN_LENGTH - 1
    )


    nextTick(() => {

        pinRefs[type][nextIndex]?.focus()

    })

}


/*
|--------------------------------------------------------------------------
| Validate PIN
|--------------------------------------------------------------------------
*/

const validatePin = () => {

    const currentPinValue = current_pin.value.join("")
    const newPinValue = pin.value.join("")
    const confirmPinValue = confirmPin.value.join("")


    /*
    |--------------------------------------------------------------------------
    | Current PIN
    |--------------------------------------------------------------------------
    */

    if (currentPinValue.length !== PIN_LENGTH) {

        pinError.value = "Please enter your current PIN"

        return false

    }


    /*
    |--------------------------------------------------------------------------
    | New PIN
    |--------------------------------------------------------------------------
    */

    if (newPinValue.length !== PIN_LENGTH) {

        pinError.value = "Please enter your new PIN"

        return false

    }


    /*
    |--------------------------------------------------------------------------
    | Confirm PIN
    |--------------------------------------------------------------------------
    */

    if (confirmPinValue.length !== PIN_LENGTH) {

        pinError.value = "Please confirm your new PIN"

        return false

    }


    /*
    |--------------------------------------------------------------------------
    | New PIN cannot equal current PIN
    |--------------------------------------------------------------------------
    */

    if (currentPinValue === newPinValue) {

        pinError.value =
            "Your new PIN must be different from your current PIN"

        return false

    }


    /*
    |--------------------------------------------------------------------------
    | New PIN must match confirmation
    |--------------------------------------------------------------------------
    */

    if (newPinValue !== confirmPinValue) {

        pinError.value = "PINs do not match"

        return false

    }


    pinError.value = ""

    return true
}


/*
|--------------------------------------------------------------------------
| Submit PIN
|--------------------------------------------------------------------------
*/

const submitPin = async () => {

    pinError.value = ""


    /*
    |--------------------------------------------------------------------------
    | Validate
    |--------------------------------------------------------------------------
    */

    if (!validatePin()) {
        return
    }


    const currentPinValue =
        current_pin.value.join("")

    const newPinValue =
        pin.value.join("")


    pinLoading.value = true


    try {

        /*
        |--------------------------------------------------------------------------
        | IMPORTANT
        |--------------------------------------------------------------------------
        | 
        | If your API function is called updateTransactionPin instead
        | of addpin, change addpin() below to updateTransactionPin().
        |
        */

        await updateTransactionPin({

            current_pin: currentPinValue,

            new_pin: newPinValue

        })


        showSnackbar(
            "PIN updated successfully",
            "success"
        )


        /*
        |--------------------------------------------------------------------------
        | Clear all PIN fields after successful update
        |--------------------------------------------------------------------------
        */

        current_pin.value =
            Array(PIN_LENGTH).fill("")

        pin.value =
            Array(PIN_LENGTH).fill("")

        confirmPin.value =
            Array(PIN_LENGTH).fill("")


        /*
        |--------------------------------------------------------------------------
        | Focus current PIN again
        |--------------------------------------------------------------------------
        */

        await nextTick()

        pinRefs.current_pin[0]?.focus()


    } catch (e) {

        const msg = getAllErrors(e)


        showSnackbar(
            msg || "PIN update failed",
            "error"
        )

    } finally {

        pinLoading.value = false

    }

}


/* =========================================================
   2FA
========================================================= */

const updating2FA = ref(false)

const twoFactorEnabled = computed({

    get: () => {
        return auth.user?.user?.two_factor_enabled ?? false
    },

    set: (value) => {

        if (auth.user?.user) {

            auth.user.user.two_factor_enabled = value

        }

    }

})


const toggleTwoFactor = async (value) => {

    const previousValue = !value

    updating2FA.value = true


    try {

        await enable2FA({

            requires_2fa: value

        })


        showSnackbar(
            "Two-factor authentication updated",
            "success"
        )


    } catch (e) {

        /*
        |--------------------------------------------------------------------------
        | Revert switch if API request fails
        |--------------------------------------------------------------------------
        */

        if (auth.user?.user) {

            auth.user.user.two_factor_enabled =
                previousValue

        }


        showSnackbar(
            "Unable to update two-factor authentication.",
            "error"
        )

    } finally {

        updating2FA.value = false

    }

}


/* =========================================================
   USER / BANK DATA
========================================================= */

const nuban = computed(() => {

    return dash.user?.user?.profile?.nuban || ""

})


const bank = computed(() => {

    return dash.user?.user?.profile?.nuban_bank || ""

})


const bankAccountName = computed(() => {

    return dash.user?.user?.profile?.nuban_account_name || ""

})


const email = computed(() => {

    return dash.user?.user?.email || ""

})


/* =========================================================
   TIN
========================================================= */

const tin = ref("")
const originalTin = ref("")
const tinLoading = ref(false)


const tinStatus = computed(() => {

    return dash.user?.user?.profile?.tin_status || ""

})


const isTinSuccessful = computed(() => {

    return tinStatus.value
        .toString()
        .toLowerCase() === "successful"

})


/*
|--------------------------------------------------------------------------
| Mask TIN
|--------------------------------------------------------------------------
*/

const maskedTin = computed(() => {

    if (
        !isTinSuccessful.value ||
        !originalTin.value
    ) {

        return ""

    }


    const value = originalTin.value.toString()


    /*
    |--------------------------------------------------------------------------
    | Keep the last 4 digits visible
    |--------------------------------------------------------------------------
    */

    if (value.length <= 4) {

        return "*".repeat(value.length)

    }


    return (
        "*".repeat(value.length - 4) +
        value.slice(-4)
    )

})


/*
|--------------------------------------------------------------------------
| TIN input
|--------------------------------------------------------------------------
*/

const handleTinInput = (event) => {

    if (isTinSuccessful.value) {
        return
    }


    tin.value = event.target.value
        .replace(/\D/g, "")

}


/* =========================================================
   PASSWORD UPDATE
========================================================= */

const handlePasswordChange = async () => {

    error.value = ""


    /*
    |--------------------------------------------------------------------------
    | Validate password
    |--------------------------------------------------------------------------
    */

    if (
        password.value.length < 8 ||
        confirmpass.value.length < 8
    ) {

        error.value =
            "Password must be at least 8 characters"

        return

    }


    /*
    |--------------------------------------------------------------------------
    | Confirm password
    |--------------------------------------------------------------------------
    */

    if (password.value !== confirmpass.value) {

        error.value =
            "Passwords do not match"

        return

    }


    passLoading.value = true


    try {

        await changePassword({

            email: email.value,
            current_password: current_password.value,
            new_password: password.value

        })


        showSnackbar(
            "Password updated successfully. Logging out...",
            "success"
        )


        /*
        |--------------------------------------------------------------------------
        | Clear password fields
        |--------------------------------------------------------------------------
        */

        password.value = ""
        confirmpass.value = ""


        setTimeout(() => {

            logout()

        }, 1000)


    } catch (e) {

        const msg = getAllErrors(e)


        showSnackbar(
            msg || "Password update failed",
            "error"
        )

    } finally {

        passLoading.value = false

    }

}


/* =========================================================
   TIN UPDATE
========================================================= */

const handleUpdateTIN = async () => {

    if (!tin.value.trim()) {

        showSnackbar(
            "Please enter your TIN",
            "error"
        )

        return

    }


    tinLoading.value = true


    try {

        await updateTin({

            tin: tin.value.trim()

        })


        showSnackbar(
            "TIN updated successfully",
            "success"
        )


        /*
        |--------------------------------------------------------------------------
        | Reload user so the latest TIN status comes from backend
        |--------------------------------------------------------------------------
        */

        await dash.loadUser()


    } catch (e) {

        const msg = getAllErrors(e)


        showSnackbar(
            msg || "TIN update failed",
            "error"
        )

    } finally {

        tinLoading.value = false

    }

}


/* =========================================================
   TIN INITIALIZATION
========================================================= */

const initializeTin = () => {

    const userTin =
        dash.user?.user?.profile?.tin || ""


    originalTin.value =
        userTin.toString()


    /*
    |--------------------------------------------------------------------------
    | If TIN is already verified, don't put actual TIN
    | inside editable input.
    |--------------------------------------------------------------------------
    */

    if (isTinSuccessful.value) {

        tin.value = ""

    } else {

        tin.value = originalTin.value

    }

}


/* =========================================================
   SIDEBAR
========================================================= */

const toggleSidebar = () => {

    if (isMobile.value) {

        drawerOpen.value =
            !drawerOpen.value

    } else {

        isSidebarCollapsed.value =
            !isSidebarCollapsed.value

    }

}


/* =========================================================
   MOBILE
========================================================= */

const checkMobile = () => {

    isMobile.value =
        window.innerWidth < 768


    drawerOpen.value =
        !isMobile.value

}


/* =========================================================
   DARK MODE
========================================================= */

const toggleDarkMode = () => {

    isDarkMode.value =
        !isDarkMode.value

}


/* =========================================================
   LOGOUT
========================================================= */

const logout = () => {

    auth.logout()

    router.push({
        name: "Login"
    })

}


/* =========================================================
   COPY
========================================================= */

const copied = ref(null)


const copyToClipboard = async (text) => {

    try {

        await navigator.clipboard.writeText(text)

        copied.value = text


        setTimeout(() => {

            copied.value = null

        }, 1500)


    } catch (err) {

        console.error(
            "Unable to copy:",
            err
        )

    }

}


/* =========================================================
   SNACKBAR
========================================================= */

const snackbar = ref({

    show: false,

    message: "",

    color: "success"

})


const showSnackbar = (
    message,
    color = "success"
) => {

    snackbar.value = {

        show: true,

        message,

        color

    }

}


/* =========================================================
   ON MOUNT
========================================================= */

onMounted(async () => {

    checkMobile()

    window.addEventListener(
        "resize",
        checkMobile
    )


    loading.value = true


    try {

        await dash.loadUser()

        initializeTin()


    } catch (e) {

        console.error(
            "Failed to load user:",
            e
        )

    } finally {

        loading.value = false

    }


    /*
    |--------------------------------------------------------------------------
    | Focus current PIN first box
    |--------------------------------------------------------------------------
    */

    await nextTick()

    pinRefs.current_pin[0]?.focus()

})


/* =========================================================
   ON UNMOUNT
========================================================= */

onUnmounted(() => {

    window.removeEventListener(
        "resize",
        checkMobile
    )

})

</script>


<style scoped>

.main {
    display: flex;
    min-height: 100vh;
}

.main-content {
    flex: 1;
    background: #f8fafc;
}

.content {
    padding-left: 10%;
    padding-right: 10%;
}


/* =========================================================
   TITLES
========================================================= */

.column-title h5 {
    font-size: 1.1rem;
    font-weight: 400;
    margin-bottom: 2.5%;
}


/* =========================================================
   INPUTS
========================================================= */

.modern-input {
    width: 100%;
    height: 46px;
    margin-top: 6px;
    padding: 10px 14px;
    border-radius: 10px;
    border: 1px solid #d1d5db;
    font-size: 15px;
    background: transparent;
    transition: all 0.2s ease;
}

.modern-input:focus {
    outline: none;
    border-color: #000;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
}

.modern-input:disabled {
    background: #f3f4f6;
    color: #6b7280;
    cursor: not-allowed;
}


/* =========================================================
   CARDS
========================================================= */

.outer-card {
    border: 1px solid #e4dede;
    border-radius: 8px;
    background: #fff;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 16px;
}

.inner-card {
    border: 1px solid #eae5e5;
    border-radius: 6px;
    padding: 8px;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
}

.inner-mini-card {
    border: 1px solid #f0f0f0;
    border-radius: 8px;
    background: #ffffff;
}


/* =========================================================
   BUTTON
========================================================= */

.action-button {
    width: 100%;
    height: 46px;
    background: #000;
    color: #fff;
    border: none;
    border-radius: 10px;
    font-weight: 600;
    transition: all 0.25s ease;
}

.action-button:hover {
    background: #292828;
}

.action-button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}


/* =========================================================
   PASSWORD
========================================================= */

.password-form {
    width: 100%;
    max-width: 420px;
    margin: 0 auto;
    padding: 20px;
}

.password-group {
    margin-bottom: 24px;
}

.password-wrapper {
    position: relative;
    width: 100%;
}

.password-input {
    width: 100%;
    padding-right: 48px;
}

.password-icon {
    position: absolute;
    top: 50%;
    right: 16px;
    transform: translateY(-50%);
    cursor: pointer;
    color: #6b7280;
    transition: color .2s;
}

.password-icon:hover {
    color: #000;
}


/* =========================================================
   PIN
========================================================= */

.pin-form {
    width: 100%;
    max-width: 420px;
    margin: 0 auto;
    padding: 20px;
}

.pin-group {
    margin-bottom: 28px;
}

.pin-wrapper {
    width: 100%;
}

.pin-container {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    width: 100%;
    margin-top: 8px;
}

.pin-box {
    width: 52px;
    height: 52px;
    border: 1px solid #d1d5db;
    border-radius: 12px;
    text-align: center;
    font-size: 22px;
    font-weight: 600;
    background: #fff;
    transition: all 0.2s ease;
    outline: none;
}

.pin-box:focus {
    border-color: #000;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, .08);
    outline: none;
}


/* =========================================================
   LOADING
========================================================= */

.spin {
    animation: spin 1s linear infinite;
}

@keyframes spin {

    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }

}


/* =========================================================
   MOBILE
========================================================= */

@media (max-width: 768px) {

    .content {
        padding-left: 2%;
        padding-right: 2%;
    }

}


@media (max-width: 480px) {

    .pin-form {
        padding: 10px 5px;
    }

    .pin-container {
        gap: 6px;
    }

    .pin-box {
        width: 44px;
        height: 44px;
        font-size: 18px;
    }

    .password-form {
        padding: 10px 5px;
    }

    .password-group {
        margin-bottom: 20px;
    }

}

</style>